import { useCallback, useMemo } from "react";

import { createError, parseGraphQLError } from "utils";

import { usePagination } from "hooks";
import useRequest from "hooks/useRequest";

import toGlobalId from "scenes/Hubly/components/Workspace/api/helper";

export function useClientComments(
  clientUuid,
  newStandaloneDraftGid = undefined,
  taskUuid = undefined,
  clientWorkflowUuid = undefined
) {
  const queryFactory = useCallback(() => {
    const query = `query getClientComments($clientGid: ID!, $excludeCommentDraftGid: ID, $taskUuid: UUID,
          $clientWorkflowUuid: UUID, $pageSize: Int, $after: String) {
        comments(clientGid: $clientGid, taskUuid: $taskUuid, excludeCommentDraftGid: $excludeCommentDraftGid, 
              clientWorkflowUuid: $clientWorkflowUuid, first: $pageSize, after: $after) {
            edges {
                node {
                    id
                    commentType
                    uuid
                    creator
                    createdAt
                    updatedAt
                    content
                    externalId
                    externalCreatedAt
                    externalUpdatedAt
                    isHidden
                    isCrmOnly
                    relatedEntityType
                    createdBy {
                        id
                        name
                        firstName
                        lastName
                        email
                        phoneNumber
                    }
                    draft {
                        id
                        content
                        updatedAt
                    }
                    taskUuid
                    clientWorkflowUuid
                }
            }
            totalCount
            pageInfo {
              hasNextPage
              endCursor
            }
        }
    }`;

    const resolver = ({ data }) => {
      return {
        data: data.comments.edges.map(({ node }) => {
          return { ...node };
        }),
        totalCount: data.comments.totalCount,
        pageInfo: data.comments.pageInfo,
      };
    };

    return { query, resolver };
  }, []);

  const { req } = useRequest(queryFactory);
  const comments = usePagination(req, {
    variables: useMemo(() => {
      return {
        clientGid: toGlobalId("Client", clientUuid),
        excludeCommentDraftGid: newStandaloneDraftGid,
        taskUuid: taskUuid,
        clientWorkflowUuid: clientWorkflowUuid,
      };
    }, [clientUuid, newStandaloneDraftGid, taskUuid, clientWorkflowUuid]),
    options: {
      pageSizes: [10, 20, 30, 100, 200],
    },
  });

  const generateErrorMsg = useCallback((error, name) => {
    return createError({
      ...parseGraphQLError(error),
      name: name,
      defaultMessage:
        "Failed to load comments. Please refresh the page or contact support for further assistance.",
    }).message;
  }, []);

  return {
    comments: comments,
    error:
      comments.error && !comments.loading
        ? generateErrorMsg(comments.error, "GetClientComments")
        : null,
    initialFetch: comments.loading && comments.fetchCount === 0,
  };
}

export default useClientComments;
